import React from 'react';
import { trackCallConversion } from '../../utils/tracking';
import './Hero.css';

function DotGrid() {
  const rows = 10;
  const cols = 16;
  const spacing = 18;
  const dots = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dist = Math.sqrt(r * r + c * c);
      const radius = Math.max(0.5, 3.0 - 0.15 * dist);
      const opacity = Math.max(0, 0.8 - 0.05 * dist);

      if (opacity > 0) {
        dots.push(
          <circle
            key={`${r}-${c}`}
            cx={c * spacing + 10}
            cy={r * spacing + 10}
            r={radius}
            fill="#e8a030"
            opacity={opacity}
          />
        );
      }
    }
  }

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${cols * spacing + 10} ${rows * spacing + 10}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      {dots}
    </svg>
  );
}

function LeftGlowingLines() {
  return (
    <div className="left-glowing-lines">
      <svg width="100%" height="100%" viewBox="0 0 300 800" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="line-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="left-line-grad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ff9d1a" stopOpacity="0.9" />
            <stop offset="15%" stopColor="#ffd700" stopOpacity="1" />
            <stop offset="45%" stopColor="#ff8c00" stopOpacity="0.5" />
            <stop offset="80%" stopColor="#ffd700" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffd700" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="flare-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="25%" stopColor="#ffd700" stopOpacity="0.8" />
            <stop offset="60%" stopColor="#ff8c00" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#ff8c00" stopOpacity="0" />
          </radialGradient>
        </defs>
        <line x1="0" y1="320" x2="50" y2="270" stroke="url(#left-line-grad)" strokeWidth="0.8" filter="url(#line-glow)" />
        <line x1="0" y1="400" x2="70" y2="330" stroke="url(#left-line-grad)" strokeWidth="1.5" filter="url(#line-glow)" />
        <line x1="0" y1="480" x2="70" y2="410" stroke="url(#left-line-grad)" strokeWidth="1.2" filter="url(#line-glow)" />
        <line x1="0" y1="560" x2="60" y2="500" stroke="url(#left-line-grad)" strokeWidth="0.9" filter="url(#line-glow)" />
        <line x1="0" y1="640" x2="50" y2="590" stroke="url(#left-line-grad)" strokeWidth="0.7" filter="url(#line-glow)" />
        <circle cx="35" cy="365" r="8" fill="url(#flare-grad)" />
        <circle cx="40" cy="440" r="9" fill="url(#flare-grad)" />
        <circle cx="30" cy="530" r="6" fill="url(#flare-grad)" />
      </svg>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero" id="hero">
      {/* Left side glowing diagonal lines decoration */}
      <LeftGlowingLines />

      {/* Corner Decoration - Top Left */}
      <div className="corner-decoration top-left">
        <DotGrid />
      </div>

      {/* Corner Decoration - Top Right */}
      <div className="corner-decoration top-right">
        <DotGrid />
      </div>

      {/* Corner Decoration - Bottom Left */}
      <div className="corner-decoration bottom-left">
        <DotGrid />
      </div>

      {/* Corner Decoration - Bottom Right */}
      <div className="corner-decoration bottom-right">
        <DotGrid />
      </div>

      <div className="hero-content">
        {/* Premium Badge */}
        <div className="premium-badge">
          <span className="star-icon">⭐</span>
          <span>Expert Device Repair</span>
        </div>

        {/* Main Heading */}
        <h1 className="hero-heading">
          <span className="white-text">Professional Device Care</span>
          <span className="orange-text">At Your Doorstep</span>
        </h1>

        {/* Subtitle */}
        <p className="hero-subtitle">
          <span className="subtitle-line">No travel. No waiting. We come to you.</span>
          <span className="subtitle-line">Certified experts, genuine parts, Upfront pricing.</span>
          <span className="subtitle-line">Reliable service designed around your convenience.</span>
        </p>

        {/* Features Row */}
        <div className="features-row">
          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 12"></polyline>
              </svg>
            </div>
            <div className="feature-text">
              <div className="feature-title">Same Day</div>
              <div className="feature-label">Service</div>
            </div>
          </div>

          <div className="feature-divider" />

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="2" x2="12" y2="4"></line>
                <line x1="10" y1="2" x2="14" y2="2"></line>
                <circle cx="12" cy="14" r="8"></circle>
                <polyline points="12 14 15 11"></polyline>
              </svg>
            </div>
            <div className="feature-text">
              <div className="feature-title">30 Mint</div>
              <div className="feature-label">Avg Repair Time</div>
            </div>
          </div>

          <div className="feature-divider" />

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <polyline points="16 11 18 13 22 9"></polyline>
              </svg>
            </div>
            <div className="feature-text">
              <div className="feature-title">Verified</div>
              <div className="feature-label">Technician</div>
            </div>
          </div>

          <div className="feature-divider" />

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <div className="feature-text">
              <div className="feature-title">Doorstep</div>
              <div className="feature-label">Service</div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="cta-row">
          <button 
            className="btn-primary" 
            id="book-repair-btn"
            onClick={() => {
              const bookingSection = document.getElementById('booking');
              if (bookingSection) {
                bookingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Book Doorstep Repair
            <span className="arrow">→</span>
          </button>
          <a
            href="tel:+918700319769"
            className="btn-secondary"
            id="call-now-btn"
            data-dsmr-phone="true"
            onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
            style={{ textDecoration: 'none' }}
          >
            <span className="phone-icon">📞</span>
            Call Now
            <span className="phone-number">+91 8700319769</span>
          </a>
        </div>

        {/* Trust Section */}
        <div className="trust-section">
          <p className="trust-label">Trusted by 10000+ Customers</p>
          <div className="brand-logos">
            <span className="brand-logo">Apple</span>
            <span className="brand-logo oneplus">
              SAMSUNG
            </span>
            <span className="brand-logo">
              GOOGLE
            </span>
            <span className="brand-logo">Xiaomi</span>
            <span className="brand-logo">Vivo</span>
            <span className="brand-logo">Asus</span>
            <span className="brand-logo">HP</span>
            <span className="brand-logo">Dell</span>
          </div>
        </div>
      </div>

      <div className="hero-image-wrap">
        <img src="/hero_item.webp" alt="Doorstep repair service" className="hero-image" />
      </div>
    </section>
  );
}

export default Hero;
